import {
  CheckExistMemberResponse,
  FetchOrganizationMemberParam,
  MemberListFetchResponse,
  SearchMemberParam,
} from '@@types/Api/Member';
import { MemberVacationSettingWrapper } from '@@types/Api/Setting';
import { ContentsWrap, ContentWrap, EMAIL, MEMBER_ID } from '@@types/common';
import Member, { SearchedMember } from '@@types/Member';
import { TenantInvitedMemberResponse } from '@@types/Tenant/TenantInvitedMemberResponse';
import { TenantInviteMember } from '@@types/Tenant/TenantInviteMember';
import Vacation from '@@types/Vacation';

import { URLS } from '~/constants';
import Api2 from '~/stores/providers/Api2';
import { isNonNil } from '~/utils/fnUtil/fnUtil';
import { mergeRight, prop } from 'ramda';
import pathSatisfies from 'ramda/es/pathSatisfies';

class MemberApi {
  constructor(private api: Api2) {}

  fetchMembers = (idList: MEMBER_ID[] = []): Promise<MemberListFetchResponse> => {
    return this.api.resultRequester.post(`${URLS.WAS_API}/members/fetch`, { idList });
  };

  fetchMemberById = (memberId: MEMBER_ID): Promise<ContentWrap<Member>> => {
    return this.api.resultRequester.cancelGet(`${URLS.WAS_API}/members/${memberId}`, '');
  };

  checkExistMemberByEmailAddress = (emailAddress: EMAIL): Promise<CheckExistMemberResponse> => {
    return this.api.ignoreErrorRequester
      .get(`${URLS.WAS_API}/members/${emailAddress}`, {
        params: {
          memberIdType: 'externalEmailAddress',
        },
      })
      .then((res) => ({
        emailAddress,
        exist: pathSatisfies(isNonNil, ['data', 'result'])(res),
      }));
  };

  fetchMemberCount = (): Promise<number> => {
    return (
      this.api.resultRequester.get(
        `${URLS.WAS_API}/members?page=0&size=1&tenantMemberRoles=owner,admin,member,subMember`
      ) as Promise<ContentsWrap<Member>>
    ).then(({ totalCount }) => totalCount);
  };

  fetchOrganizationMembers = ({ orgId, ...params }: FetchOrganizationMemberParam): Promise<ContentsWrap<Member>> => {
    return this.api.resultRequester.get(`${URLS.WAS_API}/organizations/!${orgId}/members`, {
      params: mergeRight(params, {
        order: 'name',
        tenantMemberRoles: 'owner,admin,member,dummy',
        status: 'member,dormant',
      }),
    });
  };

  fetchVacation = (memberId: MEMBER_ID) => {
    return (
      this.api.contentsRequester.get(
        `${URLS.WAS_API}/members/${memberId}/settings/common.vacation`
      ) as Promise<MemberVacationSettingWrapper>
    ).then<Vacation>(prop('value'));
  };

  inviteTenantMembers = (invites: TenantInviteMember[]): Promise<TenantInvitedMemberResponse> => {
    return this.api.resultRequester.post(`${URLS.WAS_API}/invitations`, invites);
  };

  searchMembers = ({
    keyword,
    size = 50,
    page = 0,
    boostMembersId = [],
  }: SearchMemberParam): Promise<SearchedMember[]> => {
    return this.api.contentsRequester.cancelPost(
      `${URLS.WAS_API}/members/search`,
      {
        all: keyword,
        includeDuty: true,
        typeList: ['member'],
        boost: { ids: boostMembersId },
        useMessenger: true,
      },
      '',
      { headers: { 'Dooray-Caller': 'MESSENGER' }, params: { size, page } }
    );
  };

  searchMembersWithTotalCount = ({
    keyword,
    size = 50,
    page = 0,
    boostMembersId = [],
  }: SearchMemberParam): Promise<ContentsWrap<SearchedMember[]>> => {
    return this.api.resultRequester.cancelPost(
      `${URLS.WAS_API}/members/search`,
      {
        all: keyword,
        typeList: ['member'],
        boost: { ids: boostMembersId },
        useMessenger: true,
      },
      '',
      { headers: { 'Dooray-Caller': 'MESSENGER' }, params: { size, page } }
    );
  };
}

export default MemberApi;
